import { motion } from "framer-motion";
import { PawPrint, Heart, Bone, ScribbleLine } from "./Doodles";
import buildMaterials from "@/assets/build-materials.png";

const STEPS = [
  {
    n: "01",
    title: "Build Together",
    body: "Students and friends gather for hands-on build days. No experience needed — just a willingness to pick up a drill, learn practical woodworking skills, and laugh a lot while doing it.",
    img: buildMaterials,
    alt: "Fresh timber and plywood panels loaded in a car trunk, materials for a Project Archie doghouse build day",
    rotate: "-rotate-1",
    accent: "#3B82F6",
  },
  {
    n: "02",
    title: "Donate to Shelters",
    body: "Every finished doghouse is delivered to a local dog shelter, giving dogs in need a warm, safe place of their own while they wait for their forever homes.",
    img: "https://customer-assets-wrfwihn1.emergentagent.net/job_archie-builds/artifacts/96ru5rws_ChatGPT%20Image%20Aug%2030%2C%202026%2C%2012_29_15%20AM.png",
    alt: "A hand-painted teal and grey Project Archie doghouse with paw prints and a bone motif, donated to a local dog shelter",
    rotate: "rotate-1",
    accent: "#E63946",
  },
  {
    n: "03",
    title: "Make an Impact",
    body: "Each build leaves two legacies: a doghouse that shelters a dog, and a student who walks away with new skills, new friends, and proof that small hands-on projects can change a community.",
    img: "https://customer-assets-wrfwihn1.emergentagent.net/job_archie-builds/artifacts/ceciqca9_ChatGPT%20Image%20Aug%2030%2C%202026%2C%2012_26_20%20AM.png",
    alt: "The first Project Archie doghouse prototype painted blue and yellow, proof of the impact students make for shelter dogs",
    rotate: "-rotate-1",
    accent: "#3B82F6",
  },
];

const Chapter = ({ step, index }) => {
  const flip = index % 2 === 1;
  return (
    <motion.article
      data-testid={`mission-step-${step.n}`}
      initial={{ opacity: 0, y: 60, rotate: flip ? 2 : -2 }}
      whileInView={{ opacity: 1, y: 0, rotate: 0 }}
      viewport={{ once: true, margin: "-80px" }}
      transition={{ duration: 0.8, ease: [0.22, 1, 0.36, 1] }}
      className={`flex flex-col items-center gap-10 md:gap-16 ${flip ? "md:flex-row-reverse" : "md:flex-row"}`}
    >
      <div className={`relative w-full md:w-1/2 ${step.rotate}`}>
        {step.img ? (
          <div className="relative border-2 border-[#16294B] bg-[#FBF3E3] p-3 shadow-[8px_8px_0_0_#16294B]">
            <img
              src={step.img}
              alt={step.alt}
              loading="lazy"
              className="aspect-[4/3] w-full border-2 border-[#16294B] object-cover"
            />
            {step.img2 && (
              <motion.img
                data-testid={`mission-step-${step.n}-extra-photo`}
                src={step.img2}
                alt={step.alt2}
                loading="lazy"
                initial={{ opacity: 0, scale: 0.6, rotate: 12 }}
                whileInView={{ opacity: 1, scale: 1, rotate: 6 }}
                viewport={{ once: true }}
                transition={{ duration: 0.6, delay: 0.35, ease: "backOut" }}
                className="absolute -bottom-8 -right-4 w-32 border-2 border-[#16294B] bg-[#FBF3E3] p-1.5 shadow-[5px_5px_0_0_#16294B] sm:w-40 md:-right-8"
              />
            )}
            <span className="absolute -top-5 -left-4 flex h-14 w-14 items-center justify-center rounded-full border-2 border-[#16294B] font-display text-2xl text-[#FBF3E3] shadow-[3px_3px_0_0_#16294B]" style={{ backgroundColor: step.accent }}>
              {step.n}
            </span>
          </div>
        ) : (
          <div className="relative flex aspect-[4/3] w-full items-center justify-center border-2 border-dashed border-[#16294B] bg-[#F4E9D2] shadow-[8px_8px_0_0_#16294B]">
            <span className="absolute -top-5 -left-4 flex h-14 w-14 items-center justify-center rounded-full border-2 border-[#16294B] font-display text-2xl text-[#FBF3E3] shadow-[3px_3px_0_0_#16294B]" style={{ backgroundColor: step.accent }}>
              {step.n}
            </span>
            <div className="flex flex-col items-center gap-4 px-8 text-center">
              <Heart className="h-14 w-14" />
              <p className="font-display text-3xl text-[#16294B] md:text-4xl">One doghouse at a time.</p>
              <div className="flex gap-3">
                <PawPrint className="h-7 w-7" color="#3B82F6" />
                <Bone className="h-8 w-8" color="#16294B" />
                <PawPrint className="h-7 w-7" color="#E63946" />
              </div>
            </div>
          </div>
        )}
      </div>

      <div className={`w-full md:w-1/2 ${flip ? "md:pr-4 md:text-right" : "md:pl-4"}`}>
        <span className="font-display text-lg tracking-widest text-[#E63946]">CHAPTER {step.n}</span>
        <h3 className="mt-2 font-display text-4xl text-[#16294B] md:text-5xl">{step.title}</h3>
        <div className={`mt-3 ${flip ? "md:ml-auto" : ""}`}>
          <ScribbleLine className="h-3 w-28" />
        </div>
        <p className="mt-5 text-base leading-relaxed text-[#4A5568] md:text-lg">{step.body}</p>
      </div>
    </motion.article>
  );
};

const Manifesto = () => (
  <section id="mission" data-testid="mission-section" className="relative mx-auto max-w-6xl scroll-mt-24 px-5 py-24 sm:px-8 md:py-32" aria-label="Our three-step mission">
    <motion.div
      initial={{ opacity: 0, y: 30 }}
      whileInView={{ opacity: 1, y: 0 }}
      viewport={{ once: true }}
      transition={{ duration: 0.7 }}
      className="mb-16 md:mb-24"
    >
      <div className="flex items-center gap-4">
        <PawPrint className="h-9 w-9 -rotate-12" color="#3B82F6" />
        <span className="text-xs font-bold uppercase tracking-[0.3em] text-[#E63946]">Our 3-step mission</span>
      </div>
      <h2 className="mt-4 max-w-2xl font-display text-5xl leading-tight text-[#16294B] md:text-7xl">
        How a pile of timber becomes a <span className="hl-underline">dog's new home</span>
      </h2>
    </motion.div>

    <div className="flex flex-col gap-20 md:gap-28">
      {STEPS.map((s, i) => (
        <Chapter key={s.n} step={s} index={i} />
      ))}
    </div>
  </section>
);

export default Manifesto;
